/**
 * Pool detail page — uses Card, Badge, PageHeader, AlertDialog, Skeleton.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Trophy, Users, Target, Star, Copy, Check, ArrowLeft, LogOut } from 'lucide-react';
import Button from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/card';
import Badge from '../components/ui/badge';
import PageHeader from '../components/ui/page-header';
import EmptyState from '../components/ui/empty-state';
import Skeleton from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import { usePool, useJoinPool, useLeavePool } from '../hooks/usePools';
import { useMyStats } from '../hooks/usePredictions';
import { cn, getTrendDisplay } from '../lib/utils';
import { notify } from '../lib/notify';
import { useAppStore } from '../stores/appStore';
import { classifyPoolPageErrorState } from './pool-page-error-state';

export default function PoolDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const poolSlug = slug ?? '';
  const user = useAppStore((state) => state.user);
  const { data, isLoading, error } = usePool(poolSlug);
  const { data: stats } = useMyStats(poolSlug);
  const joinPool = useJoinPool();
  const leavePool = useLeavePool();
  const [copied, setCopied] = useState(false);
  const [joinCode, setJoinCode] = useState('');

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        <Skeleton className="h-40 rounded-2xl" />
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  const errorState = classifyPoolPageErrorState({
    hasPool: Boolean(data?.pool),
    error,
  });

  if (errorState) {
    return (
      <EmptyState
        icon={<Trophy className="w-6 h-6" aria-hidden="true" />}
        title={errorState.title}
        description={errorState.description}
        action={
          <Link to="/boloes">
            <Button variant="outline">Voltar para bolões</Button>
          </Link>
        }
      />
    );
  }

  const pool = data?.pool;
  if (!pool) return null;

  const userRole = data?.userRole ?? null;
  const isMember = !!userRole;
  const isAdmin = userRole === 'admin';
  const isOwner = pool.ownerId === user?.id;

  const copyInviteCode = () => {
    if (!pool.inviteCode) return;
    navigator.clipboard
      .writeText(pool.inviteCode)
      .then(() => {
        setCopied(true);
        notify.success('Código copiado!', { duration: 1500 });
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        notify.error('Não foi possível copiar o código');
      });
  };

  const handleJoin = () => {
    if (pool.isPublic) {
      joinPool.mutate({ slug: pool.slug, code: pool.inviteCode || '' });
    } else if (joinCode) {
      joinPool.mutate({ slug: pool.slug, code: joinCode });
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="max-w-5xl mx-auto">
        <PageHeader
          back={
            <Link
              to="/boloes"
              className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para bolões
            </Link>
          }
          title={pool.name}
        />

        {/* Pool header card */}
        <Card className="p-6 mb-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-4">
              <div
                className={cn(
                  'flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl',
                  'bg-primary-100 dark:bg-primary-500/20',
                  'text-primary-700 dark:text-primary-300'
                )}
              >
                <Trophy className="h-6 w-6" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {pool.isDemo ? (
                    <Badge variant="warning">Demo</Badge>
                  ) : pool.isPublic ? (
                    <Badge variant="success">Público</Badge>
                  ) : (
                    <Badge variant="secondary">Privado</Badge>
                  )}
                  <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" aria-hidden="true" />
                    {pool.memberCount || 0}{' '}
                    {pool.memberCount === 1 ? 'participante' : 'participantes'}
                  </span>
                </div>
                {pool.description && (
                  <p className="mt-3 text-sm text-muted-foreground sm:text-[15px]">
                    {pool.description}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-shrink-0 items-center gap-2">
              {isMember ? (
                <>
                  {(isAdmin || isOwner) && pool.inviteCode && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copyInviteCode}
                          icon={copied ? <Check /> : <Copy />}
                        >
                          {copied ? 'Copiado!' : pool.inviteCode}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Copiar código de convite</TooltipContent>
                    </Tooltip>
                  )}
                  {!isOwner && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" icon={<LogOut />}>
                          Sair do bolão
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Sair do bolão?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Você perderá acesso aos palpites e ao ranking deste bolão.
                            Você pode voltar entrando novamente com o código de convite.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => leavePool.mutate(pool.slug)}
                          >
                            Confirmar saída
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2">
                  {!pool.isPublic && (
                    <input
                      type="text"
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                      placeholder="Código"
                      maxLength={6}
                      aria-label="Código de convite"
                      className="w-28 rounded-xl border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-center uppercase font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                    />
                  )}
                  <Button
                    onClick={handleJoin}
                    loading={joinPool.isPending}
                    disabled={!pool.isPublic && joinCode.length !== 6}
                  >
                    Participar
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Member stats */}
        {isMember && stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <StatCard label="Posição" value={stats.position ? `${stats.position}º` : '—'} trend={stats.trend} />
            <StatCard label="Pontos" value={stats.totalPoints} />
            <StatCard label="Placares exatos" value={stats.exactScores} />
            <StatCard
              label="Palpites"
              value={`${stats.predictionsCount}/${stats.totalMatches}`}
            />
          </div>
        )}

        {/* Section nav */}
        {isMember && (
          <div className="grid md:grid-cols-3 gap-4">
            <SectionLink
              to={`/boloes/${poolSlug}/ranking`}
              title="Ranking"
              description="Ver classificação completa"
              icon={<Trophy className="h-5 w-5" aria-hidden="true" />}
              tone="amber"
            />
            <SectionLink
              to={`/boloes/${poolSlug}/palpites`}
              title="Meus palpites"
              description="Ver e editar palpites"
              icon={<Target className="h-5 w-5" aria-hidden="true" />}
              tone="primary"
            />
            <SectionLink
              to={`/boloes/${poolSlug}/especiais`}
              title="Especiais"
              description="Campeão, artilheiro e mais"
              icon={<Star className="h-5 w-5" aria-hidden="true" />}
              tone="purple"
            />
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

function StatCard({
  label,
  value,
  trend,
}: {
  label: string;
  value: string | number;
  trend?: string;
}) {
  const trendDisplay = trend ? getTrendDisplay(trend) : null;
  return (
    <Card className="p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-foreground tabular-nums">{value}</span>
        {trendDisplay && (
          <span className={cn('text-base', trendDisplay.color)} aria-label={trendDisplay.label}>
            {trendDisplay.icon}
          </span>
        )}
      </div>
    </Card>
  );
}

function SectionLink({
  to,
  title,
  description,
  icon,
  tone,
}: {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  tone: 'amber' | 'primary' | 'purple';
}) {
  const toneClass = {
    amber:
      'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300',
    primary:
      'bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300',
    purple:
      'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300',
  }[tone];

  return (
    <Link
      to={to}
      className="group block rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <Card className="p-5 transition-all duration-200 hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-lg dark:hover:shadow-dark-lg group-hover:-translate-y-0.5">
        <CardContent className="flex items-center gap-4 p-0">
          <div className={cn('flex h-11 w-11 items-center justify-center rounded-xl', toneClass)}>
            {icon}
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
